// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

interface XProductBomActionStateType {
    checkedProducts: { [id: string]: ProductInfo };
    totalQuantity: number;
    totalCost: number;
    checkedPartNumbers: { [erp: string]: boolean };
}

export const initialProductBomActionState: XProductBomActionStateType = {
    checkedProducts: {},
    totalQuantity: 0,
    totalCost: 0,
    checkedPartNumbers: {},
};

const reducer = {
    "xProductBomAction/reset": (draft: Draft<XProductBomActionStateType>) => {
        draft.checkedProducts = {};
        draft.totalQuantity = 0;
        draft.totalCost = 0;
        draft.checkedPartNumbers = {};
    },
    "xProductBomAction/updateSelection": (
        draft: Draft<XProductBomActionStateType>,
        action: { productInfo: ProductInfo; product: ProductModel },
    ) => {
        if (draft.checkedProducts[action.productInfo.productId]) {
            delete draft.checkedProducts[action.productInfo.productId];
            delete draft.checkedPartNumbers[action.product.productNumber];
        } else {
            draft.checkedProducts[action.productInfo.productId] = action.productInfo;
            draft.checkedPartNumbers[action.product.productNumber] = true;
        }
        let totalQty = 0;
        let totalCost = 0;

        Object.values(draft.checkedProducts).forEach(product => {
            totalQty += product.qtyOrdered || 0;
            totalCost += (product.pricing?.unitNetPrice || 0) * product.qtyOrdered;
        });
        draft.totalQuantity = totalQty;
        draft.totalCost = totalCost;
    },
    "xProductBomAction/selectProduct": (
        draft: Draft<XProductBomActionStateType>,
        action: { productInfo: ProductInfo; product: ProductModel },
    ) => {
        draft.checkedProducts[action.productInfo.productId] = action.productInfo;
        draft.checkedPartNumbers[action.product.productNumber] = true;

        let totalQty = 0;
        let totalCost = 0;

        Object.values(draft.checkedProducts).forEach(product => {
            totalQty += product.qtyOrdered || 0;
            totalCost += (product.pricing?.unitNetPrice || 0) * product.qtyOrdered;
        });
        draft.totalQuantity = totalQty;
        draft.totalCost = totalCost;
    },
    "xProductBomAction/updateProduct": (
        draft: Draft<XProductBomActionStateType>,
        action: { productInfo: ProductInfo },
    ) => {
        if (Object.keys(draft.checkedProducts).includes(action.productInfo.productId)) {
            // call when qty or uom changed...
            draft.checkedProducts[action.productInfo.productId] = action.productInfo;
            let totalQty = 0;
            let totalCost = 0;
            Object.values(draft.checkedProducts).forEach(product => {
                totalQty += product.qtyOrdered || 0;
                totalCost += (product.pricing?.unitNetPrice || 0) * product.qtyOrdered;
            });
            draft.totalQuantity = totalQty;
            draft.totalCost = totalCost;
        }
    },
};

const xProductBomAction = createTypedReducerWithImmer(initialProductBomActionState, reducer);
(reducers as any).xProductBomAction = xProductBomAction;

export type xProductBomActionState = {
    xProductBomAction: XProductBomActionStateType;
} & ApplicationState;

type xProductBomActionReducers = {
    [key: string]: typeof xProductBomAction;
};

export type xProductBomActionActions =
    | Parameters<xProductBomActionReducers[keyof xProductBomActionReducers]>[1]
    | AnyAction;
